import React, { useState, useMemo } from "react";
import {
  Box,
  ClickAwayListener,
  Collapse,
  FormControl,
  Link,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  BCPAngleDown,
  BCPAngleUp,
} from "../../../../components/icons/bcp/BCPIcons";
const VIEW_OPTIONS = {
  FACTION: "faction",
  PARENT: "parent",
  NESTED: "nested",
};

const SORT_OPTIONS = {
  ALPHABETICAL: "alphabetical",
  HIGH_TO_LOW: "highToLow",
  LOW_TO_HIGH: "lowToHigh",
};

const getColor = (rank, total) => {
  const ratio = total > 1 ? rank / (total - 1) : 0;
  const l = 35 + ratio * 35;
  return `hsl(270, 65%, ${l}%)`;
};

const LeaderHoverCard = ({ subFaction, theme }) => (
  <Box
    sx={{
      background: theme.palette.mode === "dark" ? "#1a202c" : "#ffffff",
      color: theme.palette.text.primary,
      padding: "10px 14px",
      borderRadius: 1,
      boxShadow: "0 3px 9px rgba(0,0,0,0.5)",
      minWidth: 160,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: 0.5,
          backgroundColor: subFaction.color,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {subFaction.name}
      </Typography>
    </Box>
    <Typography variant="body2">Players: {subFaction.count}</Typography>
    <Typography variant="body2">{subFaction.percentage}%</Typography>
  </Box>
);

const BarTrack = ({ children, theme, sx }) => (
  <Box
    sx={{
      height: { xs: 12 },
      borderRadius: 4,
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.1)",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      ...sx,
    }}
  >
    {children}
  </Box>
);

function FactionRow({ faction, theme }) {
  return (
    <Box sx={{ mb: { xs: 2.5, sm: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: { xs: 1, sm: 0.75 },
          flexWrap: "wrap",
        }}
      >
        <Link
          // TO DO : Add link to specific faction page when the actual link is known
          // href={`/armies/${faction.id}`}
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {faction.name}
        </Link>
        <Typography variant="body2" color="text.secondary">
          ({faction.percentage}%) &bull; {faction.count} Players
        </Typography>
      </Box>
      <BarTrack theme={theme}>
        <Box
          sx={{
            height: "100%",
            width: `${faction.percentage}%`,
            backgroundColor: faction.color,
            borderRadius: 4,
            transition: "width 0.4s ease",
          }}
        />
      </BarTrack>
    </Box>
  );
}

function ParentFactionRow({ parent, expanded, onToggle, theme, total }) {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const sortedSubFactions = [...parent.subFactions].sort(
    (a, b) => b.count - a.count
  );

  const handleSegmentTap = (e, subId) => {
    e.stopPropagation();
    setOpenTooltipId((prev) => (prev === subId ? null : subId));
  };

  return (
    <ClickAwayListener onClickAway={() => setOpenTooltipId(null)}>
    <Box sx={{ mb: { xs: 2.5, sm: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: { xs: 1, sm: 0.75 },
        }}
        onClick={onToggle}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0.25, sm: 1 },
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: theme.palette.text.primary }}
          >
            {parent.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({parent.percentage}%) &bull; {parent.count} Players
          </Typography>
        </Box>
        <Box
          sx={{
            color: theme.palette.text.secondary,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            ml: 1,
          }}
        >
          {expanded ? <BCPAngleUp /> : <BCPAngleDown />}
        </Box>
      </Box>

      <BarTrack theme={theme}>
          {sortedSubFactions.map((sub) => {
            const segmentWidth = total > 0 ? (sub.count / total) * 100 : 0;
            return (
              <Tooltip
                key={sub.id}
                title={<LeaderHoverCard subFaction={sub} theme={theme} />}
                open={isMobile ? openTooltipId === sub.id : undefined}
                disableHoverListener={isMobile}
                disableFocusListener={isMobile}
                disableTouchListener
                componentsProps={{
                  tooltip: { sx: { backgroundColor: "transparent", p: 0 } },
                }}
              >
                <Box
                  onClick={isMobile ? (e) => handleSegmentTap(e, sub.id) : undefined}
                  sx={{
                    height: "100%",
                    width: `${segmentWidth}%`,
                    backgroundColor: sub.color,
                    transition: "opacity 0.2s",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.8 },
                  }}
                />
              </Tooltip>
            );
          })}
        </BarTrack>

      <Collapse in={expanded}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
            mt: { xs: 1.25, sm: 1 },
            pl: 0.5,
          }}
        >
          {sortedSubFactions.map((sub) => (
            <Box
              key={sub.id}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: sub.color,
                  flexShrink: 0,
                }}
              />
              <Link
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  textDecoration: "none",
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {sub.name}
              </Link>
              <Typography variant="caption" color="text.secondary">
                ({sub.percentage}%)
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
    </ClickAwayListener>
  );
}

export default function FactionBreakdownBarChart({ players, theme }) {
  const [viewMode, setViewMode] = useState(VIEW_OPTIONS.FACTION);
  const [sortMode, setSortMode] = useState(SORT_OPTIONS.HIGH_TO_LOW);
  const [expandedParents, setExpandedParents] = useState(new Set());

  const hasParentFactions = useMemo(
    () => players.some((p) => p.faction?.parentFaction?.id),
    [players]
  );

  const factionData = useMemo(() => {
    if (!players?.length) return [];

    const total = players.length;
    const factionMap = {};

    players.forEach((player) => {
      const faction = player.faction;
      if (!faction) return;

      const key = faction.id;
      if (!factionMap[key]) {
        factionMap[key] = {
          id: faction.id,
          name: faction.name,
          count: 0,
        };
      }
      factionMap[key].count += 1;
    });

    const factions = Object.values(factionMap).map((f) => ({
      ...f,
      percentage: parseFloat(((f.count / total) * 100).toFixed(1)),
    }));

    const sortedByCount = [...factions].sort((a, b) => b.count - a.count);
    const colorMap = {};
    sortedByCount.forEach((f, idx) => {
      colorMap[f.id] = getColor(idx, sortedByCount.length);
    });

    return factions.map((f) => ({ ...f, color: colorMap[f.id] }));
  }, [players]);

  const parentData = useMemo(() => {
    if (!players?.length) return [];

    const total = players.length;
    const parentMap = {};

    players.forEach((player) => {
      const faction = player.faction;
      if (!faction) return;

      const parentFaction = faction.parentFaction;
      const key = parentFaction?.id || faction.id;

      if (!parentMap[key]) {
        parentMap[key] = {
          id: parentFaction?.id || faction.id,
          name: parentFaction?.name || faction.name,
          count: 0,
        };
      }
      parentMap[key].count += 1;
    });

    const parents = Object.values(parentMap).map((p) => ({
      ...p,
      percentage: parseFloat(((p.count / total) * 100).toFixed(1)),
    }));

    const sortedByCount = [...parents].sort((a, b) => b.count - a.count);
    const colorMap = {};
    sortedByCount.forEach((p, idx) => {
      colorMap[p.id] = getColor(idx, sortedByCount.length);
    });

    return parents.map((p) => ({ ...p, color: colorMap[p.id] }));
  }, [players]);

  const nestedData = useMemo(() => {
    if (!players?.length) return [];

    const total = players.length;
    const parentMap = {};

    players.forEach((player) => {
      const faction = player.faction;
      if (!faction) return;

      const parentFaction = faction.parentFaction;
      const parentKey = parentFaction?.id || faction.id;

      if (!parentMap[parentKey]) {
        parentMap[parentKey] = {
          id: parentFaction?.id || faction.id,
          name: parentFaction?.name || faction.name,
          count: 0,
          subFactions: {},
        };
      }
      parentMap[parentKey].count += 1;

      const subKey = faction.id;
      if (!parentMap[parentKey].subFactions[subKey]) {
        parentMap[parentKey].subFactions[subKey] = {
          id: faction.id,
          name: faction.name,
          count: 0,
          players: [],
        };
      }
      parentMap[parentKey].subFactions[subKey].count += 1;
      parentMap[parentKey].subFactions[subKey].players.push(player);
    });

    return Object.values(parentMap).map((parent) => {
      const subFactionList = Object.values(parent.subFactions);
      const sortedSubs = [...subFactionList].sort((a, b) => b.count - a.count);

      return {
        ...parent,
        percentage: parseFloat(((parent.count / total) * 100).toFixed(1)),
        subFactions: subFactionList.map((sub) => {
          const rank = sortedSubs.findIndex((s) => s.id === sub.id);
          return {
            ...sub,
            percentage: parseFloat(((sub.count / total) * 100).toFixed(1)),
            color: getColor(rank, sortedSubs.length),
            leader: sub.players.reduce((best, player) => {
              if (!best) return player;
              if (player.placing != null && best.placing != null) {
                return player.placing < best.placing ? player : best;
              }
              if (player.placing != null) return player;
              return best;
            }, null),
          };
        }),
      };
    });
  }, [players]);

  const sortedData = useMemo(() => {
    const data =
      viewMode === VIEW_OPTIONS.FACTION
        ? factionData
        : viewMode === VIEW_OPTIONS.PARENT
          ? parentData
          : nestedData;
    if (sortMode === SORT_OPTIONS.ALPHABETICAL) {
      return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (sortMode === SORT_OPTIONS.HIGH_TO_LOW) {
      return [...data].sort((a, b) => b.count - a.count);
    }
    if (sortMode === SORT_OPTIONS.LOW_TO_HIGH) {
      return [...data].sort((a, b) => a.count - b.count);
    }
    return data;
  }, [factionData, parentData, nestedData, viewMode, sortMode]);

  const toggleExpand = (parentId) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  if (!players?.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No player data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 0 } }}>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textAlign: "center",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          pt: 2,
        }}
      >
        Faction Breakdown
      </Typography>

      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
        }}
      >
        {hasParentFactions && (
          <FormControl size="small" sx={{ minWidth: { sm: 200 } }}>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                color: theme.palette.text.primary,
              }}
            >
              <MenuItem value={VIEW_OPTIONS.PARENT}>Parent Factions</MenuItem>
              <MenuItem value={VIEW_OPTIONS.FACTION}>Factions</MenuItem>
              <MenuItem value={VIEW_OPTIONS.NESTED}>
                Nested (Parent + Sub-Factions)
              </MenuItem>
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: { sm: 230 } }}>
          <Select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
              color: theme.palette.text.primary,
            }}
          >
            <MenuItem value={SORT_OPTIONS.ALPHABETICAL}>Alphabetical</MenuItem>
            <MenuItem value={SORT_OPTIONS.HIGH_TO_LOW}>
              Player Number High to Low
            </MenuItem>
            <MenuItem value={SORT_OPTIONS.LOW_TO_HIGH}>
              Player Number Low to High
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)",
          borderRadius: 1,
          p: { xs: 1.5, sm: 2 },
        }}
      >
        {viewMode !== VIEW_OPTIONS.NESTED
          ? sortedData.map((faction) => (
              <FactionRow key={faction.id} faction={faction} theme={theme} />
            ))
          : sortedData.map((parent) => (
              <ParentFactionRow
                key={parent.id}
                parent={parent}
                expanded={expandedParents.has(parent.id)}
                onToggle={() => toggleExpand(parent.id)}
                theme={theme}
                total={players.length}
              />
            ))}
      </Box>
    </Box>
  );
}
